import formatMessage from 'format-message';

class GeminiLLMExtension {
    constructor (runtime) {
        this.runtime = runtime;
        this.apiKey = '';
        this.lastResponse = '';
        this.temperature = 0.7;
        this.maxOutputTokens = 300;
    }

    getInfo () {
        return {
            id: 'geminiLLM',
            name: formatMessage({
                id: 'gui.extension.geminiLLM.name',
                default: 'Gemini AI'
            }),
            color1: '#1A73E8',
            color2: '#1557B0',
            blocks: [
                {
                    opcode: 'setApiKey',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.setApiKey', default: 'set user API key of Gemini to [API_KEY]'}),
                    arguments: {
                        API_KEY: {
                            type: 'string',
                            defaultValue: 'YOUR_API_KEY'
                        }
                    }
                },
                {
                    opcode: 'setCreativity',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.setRandomness', default: 'set randomness and creativity of Gemini [VALUE]'}),
                    arguments: {
                        VALUE: {
                            type: 'number',
                            defaultValue: 0.4
                        }
                    }
                },
                {
                    opcode: 'setMaxTokens',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.setMaxTokens', default: 'set the maximum token value [VALUE]'}),
                    arguments: {
                        VALUE: {
                            type: 'number',
                            defaultValue: 300
                        }
                    }
                },
                {
                    opcode: 'askGemini',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.ask', default: 'ask [BOT_TYPE] [TEXT]'}),
                    arguments: {
                        BOT_TYPE: {
                            type: 'string',
                            menu: 'botTypes',
                            defaultValue: 'Normal AI'
                        },
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Hello AI'
                        }
                    }
                },
                {
                    opcode: 'defineText',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.define', default: 'define [TEXT]'}),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'mango'
                        }
                    }
                },
                {
                    opcode: 'convertToEmojis',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.movieToEmoji', default: 'convert movie to emojis [TEXT]'}),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Batman'
                        }
                    }
                },
                {
                    opcode: 'translateText',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.translate', default: 'translate [TEXT] to [LANGUAGE]'}),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Hello'
                        },
                        LANGUAGE: {
                            type: 'string',
                            menu: 'languages',
                            defaultValue: 'Spanish'
                        }
                    }
                },
                {
                    opcode: 'getPOS',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.partsOfSpeech', default: 'get parts of speech of [TEXT]'}),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'He runs fast'
                        }
                    }
                },
                {
                    opcode: 'getSynonymAntonym',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.synonyms', default: 'get [TYPE] of [TEXT]'}),
                    arguments: {
                        TYPE: {
                            type: 'string',
                            menu: 'synonymAntonym',
                            defaultValue: 'synonyms'
                        },
                        TEXT: {
                            type: 'string',
                            defaultValue: 'happy'
                        }
                    }
                },
                {
                    opcode: 'simplifyText',
                    blockType: 'command',
                    text: formatMessage({id: 'geminiLLM.blocks.summarize', default: 'get an easily understandable text of [TEXT]'}),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Photosynthesis'
                        }
                    }
                },
                {
                    opcode: 'getAIResponse',
                    blockType: 'reporter',
                    text: formatMessage({id: 'geminiLLM.blocks.getResponse', default: 'get AI response'})
                }
            ],
            menus: {
                botTypes: {
                    acceptReporters: true,
                    items: ['Normal AI', 'Sarcastic AI', 'Friend AI']
                },
                synonymAntonym: {
                    acceptReporters: true,
                    items: ['synonyms', 'antonyms']
                },
                languages: {
                    acceptReporters: true,
                    items: ['Spanish', 'French', 'Hindi', 'German', 'Chinese', 'Japanese', 'Arabic']
                }
            }
        };
    }

    setApiKey (args) {
        this.apiKey = args.API_KEY;
    }

    setCreativity (args) {
        let val = Number(args.VALUE);
        if (isNaN(val)) val = 0.7;
        if (val < 0) val = 0;
        if (val > 1) val = 1;
        this.temperature = val;
    }

    setMaxTokens (args) {
        let val = parseInt(args.VALUE, 10);
        if (isNaN(val) || val <= 0) val = 300;
        this.maxOutputTokens = val;
    }

    async _callGemini(prompt, systemInstruction = null) {
        if (!this.apiKey || this.apiKey === 'YOUR_API_KEY') {
            this.lastResponse = 'Error: API key is not set';
            return;
        }

        const requestBody = {
            contents: [
                {
                    parts: [
                        { text: prompt }
                    ]
                }
            ],
            generationConfig: {
                temperature: this.temperature,
                maxOutputTokens: this.maxOutputTokens
            }
        };

        if (systemInstruction) {
            requestBody.system_instruction = {
                parts: [
                    { text: systemInstruction }
                ]
            };
        }

        try {
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${this.apiKey}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            const data = await response.json();
            
            if (data.error) {
                console.error('[GeminiLLM] API Error:', data.error);
                this.lastResponse = `Error: ${data.error.message}`;
                return;
            }

            if (data.candidates && data.candidates.length > 0) {
                this.lastResponse = data.candidates[0].content.parts[0].text;
                return;
            }
            
            this.lastResponse = 'Error: No response from model';
        } catch (error) {
            console.error('[GeminiLLM] Network Error:', error);
            this.lastResponse = 'Error: Could not connect to Gemini';
        }
    }

    async askGemini (args) {
        let systemInstruction = null;
        if (args.BOT_TYPE === 'Sarcastic AI') {
            systemInstruction = 'You are a highly sarcastic AI bot. Respond to all queries with heavy sarcasm.';
        } else if (args.BOT_TYPE === 'Friend AI') {
            systemInstruction = 'You are a warm, supportive, and friendly AI bot who acts like a best friend.';
        } else {
            systemInstruction = 'You are a helpful and clear AI assistant.';
        }
        
        await this._callGemini(args.TEXT || '', systemInstruction);
    }

    async defineText (args) {
        const instruction = 'You are a dictionary. Define the text provided concisely.';
        await this._callGemini(`Define: ${args.TEXT || ''}`, instruction);
    }

    async convertToEmojis (args) {
        const instruction = 'You are an emoji translator. Represent the provided movie title or text using ONLY emojis. Do not output any regular text.';
        await this._callGemini(`Convert this to emojis: ${args.TEXT || ''}`, instruction);
    }

    async translateText (args) {
        const instruction = `You are a translator. Translate the text into ${args.LANGUAGE}. Only output the translated text.`;
        await this._callGemini(`Translate: ${args.TEXT || ''}`, instruction);
    }

    async getPOS (args) {
        const instruction = 'Analyze the parts of speech of the provided text. Return the nouns, pronouns, verbs, adverbs, adjectives, prepositions, conjunctions, and interjections in a structured list.';
        await this._callGemini(`Identify parts of speech for: ${args.TEXT || ''}`, instruction);
    }

    async getSynonymAntonym (args) {
        const instruction = `You are a thesaurus. Provide a list of ${args.TYPE} for the given word.`;
        await this._callGemini(`Provide ${args.TYPE} for: ${args.TEXT || ''}`, instruction);
    }

    async simplifyText (args) {
        const instruction = 'Explain the provided text in an easily understandable way, as if speaking to a young student or a beginner.';
        await this._callGemini(`Explain simply: ${args.TEXT || ''}`, instruction);
    }

    getAIResponse () {
        return this.lastResponse;
    }
}

export default GeminiLLMExtension;
